import React, { useEffect, useMemo, useState } from 'react';
import SeatsReservation from '../../../util/Seats6M';
import { useMutation } from 'react-query';
import { map6MResults } from '../../../util/map6MResults';
import * as TFF from '@tff/types/TFF';
import { LoadingContext } from '../../../contexts/LoadingContextProvider';
import { Seat6MComponent } from '../../../util';
import { create6MSeatComponentConfig } from '../../../util/map6MSeats';
import { SeatDetails } from '@tff/types/TFF/tfa-amend-ancillary-service';
import { useBookingContext } from '../../../hooks/use-booking-context';

interface props {
  orderDetails: TFF.OrderDetails;
  recordLocator: string;
  offerFlights: TFF.Journey[];
  setAncillarySeats: (value: any) => void;
  setSeatRows: (value: any) => void;
  setOpenSeats: (value: boolean) => void;
  openSeats: boolean;
}

type SeatMap = { [paxId: string]: SeatDetails };

export const AddSeats: React.FC<props> = ({
  orderDetails,
  offerFlights,
  recordLocator,
  openSeats,
  setOpenSeats,
  setAncillarySeats,
  setSeatRows,
}) => {
  const [scope, setScope] = useState<string>('*');
  const { showLoading, closeLoading } = React.useContext(LoadingContext);
  const { currency } = useBookingContext();

  const seatsConfig = useMemo(
    () => create6MSeatComponentConfig(offerFlights, orderDetails, currency),
    [offerFlights, orderDetails],
  );

  const ancillaries: TFF.Ancillary[] | undefined = orderDetails?.Ancillaries
    ? Object.values(orderDetails.Ancillaries)
    : [];

  const updateAncillarySeats = (paxData: { paxId: string; addSeats?: SeatDetails[] }[]) => {
    const updatedSeats: { [key: string]: { code: string; segmentOnd: string } } = {};
    
    paxData.forEach(item => {
      if (item.addSeats && item.addSeats.length > 0) {
        item.addSeats.forEach(seat => {
          const key = `${item.paxId}-${seat.segmentOnd}`;
          updatedSeats[key] = {
            code: seat.code,
            segmentOnd: seat.segmentOnd
          };
        });
      }
    });

    setAncillarySeats(updatedSeats);
  };

  const updateAncillaries = async data => {
    const seatAncillaries = ancillaries.filter(
      (a: TFF.Ancillary) => orderDetails.Journeys.map(i => i.Ond).includes(a.JourneyOnd) && a.Type === 'SEAT',
    );
    const mapData = map6MResults(data.flights, recordLocator, seatAncillaries);
    updateAncillarySeats(mapData.paxData);

    setSeatRows({
      outboundRows: data.flights[0].passengers.map(passenger => ({
        label: 'SEAT',
        originalPrice: 0,
        newPrice: passenger.seat.price.amount,
        surcharge: passenger.seat.price.amount,
        original: `0 x ${passenger.seat.seatCategory ?? 'Seat'}`,
        new: `1 x ${passenger.seat.seatCategory ?? 'Seat'} - ${passenger.seat.row}${passenger.seat.column}`,
        code: `${offerFlights[0].Ond}-${passenger.seat.row}${passenger.seat.column}`,
      })),
      inboundRows:
        data.flights.length > 1
          ? data.flights[1].passengers.map(passenger => ({
              label: 'SEAT',
              originalPrice: 0,
              newPrice: passenger.seat.price.amount,
              surcharge: passenger.seat.price.amount,
              original: `0 x ${passenger.seat.seatCategory ?? 'Seat'}`,
              new: `1 x ${passenger.seat.seatCategory ?? 'Seat'}-${passenger.seat.row}${passenger.seat.column}`,
              code: `${offerFlights[1].Ond}-${passenger.seat.row}${passenger.seat.column}`,
            }))
          : [],
    });
  };

  const { mutate, isLoading } = useMutation(updateAncillaries);

  useEffect(() => {
    isLoading ? showLoading('Saving Changes') : closeLoading();
  }, [isLoading]);

  const handleCloseSeatsReservation = (seatReservationResult?: Seat6MComponent) => {
    setOpenSeats(false);
    setScope(prev => (prev === '*' ? '+' : '*'));
    mutate(seatReservationResult);
  };

  return (
    <>
      <SeatsReservation
        initialOpen={openSeats}
        onClose={handleCloseSeatsReservation}
        scope={scope}
        configs={seatsConfig}
      />
    </>
  );
};
